/**
 * API Utility Functions
 * Provides configuration for API endpoints
 */

import { getCurrentWarehouse } from '../services/warehouseService';


const ZAZ_REGION_CODES = new Set(['ZAZ', 'DUB']);

/**
 * Whether the given host is itself a ZAZ (eu-south-2) deployment - either the
 * corp Midway domain (eligibility.eu-south-2-<stage>.picking.aft.amazon.dev) or
 * the AFT-X externalized domain (fc-picking-eligibility.[onebox.]zaz.aft.amazonoperations.app,
 * see getAftxDomainName's 'zaz' prefix for eu-south-2 in aftx-config.ts).
 */
function isServedFromZaz(host: string): boolean {
    return /^eligibility\.eu-south-2-/i.test(host) || /\.zaz\.aft\.amazonoperations\.app$/i.test(host);
}

/**
 * Get the API Gateway URL.
 */
export function getApiGatewayUrl(): string {
    const currentHost = window.location.host;

    // Local development → talk to the local service directly.
    if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
        return 'http://localhost:8080';
    }

    const warehouse = getCurrentWarehouse();
    const warehouseIsZaz = ZAZ_REGION_CODES.has(warehouse.regionCode.toUpperCase());

    // The /api-zaz CloudFront behavior is a cross-region proxy that only exists
    // on the IAD (us-east-1) distribution, so IAD can reach a ZAZ warehouse's
    // backend. It does NOT exist on ZAZ's own distribution. So only route to
    // /api-zaz when the warehouse is ZAZ/DUB AND we're being served from a
    // DIFFERENT region's distribution (i.e. proxying in from IAD). If we're
    // already on ZAZ's own site, its backend is same-origin at /api - hitting
    // /api-zaz there falls through to no matching behavior and 403s.
    if (warehouseIsZaz && !isServedFromZaz(currentHost)) {
        return '/api-zaz';
    }

    // Any deployed host (the main picking.aft.amazon.dev site AND the AFTX/FCMenu
    // externalized fc-picking-eligibility.<region>.aft.amazonoperations.app domain)
    // serves the API same-origin via the CloudFront /api/* behavior, so use the
    // relative /api path. Matching only picking.aft.amazon.dev previously sent the
    // AFTX domain to the localhost fallback, breaking every API call there.
    return '/api';
}

/**
 * Get the warehouse ID
 * Matches AngularJS utilService.getWarehouseId()
 *
 * Gets the currently selected warehouse from the warehouse selector
 */
export function getWarehouseId(): string {
    const warehouse = getCurrentWarehouse();
    return warehouse.warehouseId;
}

/**
 * Build the complete API URL for cluster endpoints
 * Smithy route: /{warehouseId}/Cluster/{endpoint}
 * Example: https://db5jy3laol.execute-api.us-west-2.amazonaws.com/beta/IND1/Cluster/GetAllClusters
 */
export function getClusterApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/Cluster/${endpoint}`;
}

/**
 * Build the complete API URL for process path eligibility endpoints
 * Smithy route: /{warehouseId}/Eligibility/{endpoint}
 * Example: https://db5jy3laol.execute-api.us-west-2.amazonaws.com/beta/IND1/Eligibility/GetAllProcessPathEligibilities
 */
export function getProcessPathApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/Eligibility/${endpoint}`;
}

/**
 * Build the complete API URL for employee endpoints
 * Smithy route: /{warehouseId}/Employee/{endpoint}
 */
export function getEmployeeApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/Employee/${endpoint}`;
}

/**
 * Build the complete API URL for permission endpoints
 * Smithy route: /{warehouseId}/Permission/{endpoint}
 */
export function getPermissionApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId()

    return `${apiGatewayUrl}/${warehouseId}/Permission/${endpoint}`;
}

/**
 * Build the complete API URL for picker endpoints
 * Smithy route: /{warehouseId}/Picker/{endpoint}
 */
export function getPickerApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/Picker/${endpoint}`;
}

/**
 * Build the complete API URL for pick area endpoints
 * Smithy route: /{warehouseId}/PickArea/{endpoint}
 */
export function getPickAreaApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/PickArea/${endpoint}`;
}

/**
 * Build the complete API URL for process path status endpoints
 * Smithy route: /{warehouseId}/ProcessPath/{endpoint}
 */
export function getProcessPathStatusApiUrl(endpoint: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/ProcessPath/${endpoint}`;
}

/**
 * Build the complete API URL for configuration endpoints
 * Smithy route: /{warehouseId}/Configuration/{endpoint}
 */
export function getConfigurationApiUrl(endpoint: string, warehouseIdOverride?: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const warehouseId = warehouseIdOverride ?? getWarehouseId();

    return `${apiGatewayUrl}/${warehouseId}/Configuration/${endpoint}`;
}

/**
 * Get authenticated user ID
 * Matches AngularJS utilService.getAuthenticatedUserId()
 * Returns a Promise for async auth operations
 */
export async function getAuthenticatedUserId(): Promise<string> {
    const { getCurrentUser } = await import('../services/employeeService');
    return getCurrentUser();
}
